// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — how much stream media this browser loaded today (UTC), for the
 * plans with a daily cap (Plus: 1 GB with one package, 2 GB with all four;
 * owner, 2026-10-06). The DASH player reports each media segment it
 * loads (`hlsService.ts`); `planAccess.streamQuotaReached` compares the
 * day's total with the plan's cap before a new stream opens, so a stream
 * already playing finishes.
 *
 * This is the app's own, soft meter: it lives in this browser and resets at
 * midnight UTC. The hard cap is the stream host's, per licence, once the
 * gateway enforces (`docs/AYNI_XR_INDIVIDUAL_PLAN.md`, phase 5).
 */

const KEY = 'ayni-stream-day.v1'

interface StreamDay {
  day: string
  bytes: number
}

/** The UTC day of a moment, `YYYY-MM-DD`. */
export function utcDay(nowMs: number): string {
  return new Date(nowMs).toISOString().slice(0, 10)
}

/** The next midnight UTC after a moment, epoch ms: when the day's count starts again. */
export function nextUtcMidnight(nowMs: number): number {
  const d = new Date(nowMs)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1)
}

/** Kept here too when the browser stores nothing (a private window). */
let memory: StreamDay | null = null

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function read(store: Storage | null): StreamDay | null {
  try {
    const raw = store?.getItem(KEY)
    const parsed = raw ? (JSON.parse(raw) as Partial<StreamDay>) : null
    if (parsed && typeof parsed.day === 'string' && typeof parsed.bytes === 'number' && parsed.bytes >= 0) return { day: parsed.day, bytes: parsed.bytes }
  } catch {
    /* unreadable: fall back to memory */
  }
  return memory
}

/** Bytes of stream media loaded so far today. */
export function streamBytesToday(nowMs: number = Date.now(), store: Storage | null = storage()): number {
  const day = read(store)
  return day && day.day === utcDay(nowMs) ? day.bytes : 0
}

/** Add one loaded media segment to today's count. */
export function recordStreamBytes(bytes: number, nowMs: number = Date.now(), store: Storage | null = storage()): void {
  if (!(bytes > 0) || !Number.isFinite(bytes)) return
  const next: StreamDay = { day: utcDay(nowMs), bytes: streamBytesToday(nowMs, store) + bytes }
  memory = next
  try {
    store?.setItem(KEY, JSON.stringify(next))
  } catch {
    /* full or private: the memory copy lasts this page */
  }
}

/** For tests: forget today's count. */
export function resetStreamQuotaForTests(): void {
  memory = null
  try {
    storage()?.removeItem(KEY)
  } catch {
    /* nothing to clear */
  }
}
